import { z } from "zod";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";

export declare namespace recordImageLoadDiagnostic {
  export type { Event };
}

/** 異常とその復旧だけを Console へ出力する。DOM・URL・ID・本文・例外は出力しない。 */
export function recordImageLoadDiagnostic(
  event: Event,
  image: HTMLImageElement,
  retries: number,
  elapsedMs: number
): void {
  try {
    const now = Date.now();
    // 多数の画像が同時に失敗しても、診断処理で操作を重くしない。
    if (now - rateWindowStartedAt >= 60_000 || now < rateWindowStartedAt) {
      rateWindowStartedAt = now;
      recordsInWindow = 0;
    }
    if (recordsInWindow >= 30) return;
    recordsInWindow += 1;
    const objects = Object.values(useCanvasDocStore.getState().objects);
    const source = image.getAttribute("src") ?? "";
    const version = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown";
    const candidate = RecordSchema.safeParse({
      appVersion: VERSION_PATTERN.test(version) ? version : "unknown",
      complete: image.complete,
      displayHeight: image.height,
      displayWidth: image.width,
      elapsedMs: Math.max(0, Math.round(elapsedMs)),
      event,
      imageCount: objects.filter((object) => object.type === "image").length,
      naturalHeight: image.naturalHeight,
      naturalWidth: image.naturalWidth,
      objectCount: objects.length,
      online: navigator.onLine,
      retries,
      sourceKind: source.startsWith("data:")
        ? "placeholder"
        : source.startsWith("blob:")
          ? "blob"
          : "remote",
      visibility: document.visibilityState === "hidden" ? "hidden" : "visible",
      zoom: useCanvasStore.getState().view.zoom,
    });
    if (!candidate.success) return;
    const record = candidate.data;
    // DOM や可変オブジェクトを渡さず、その時点の安全なプリミティブだけを固定する。
    const message = `[CanvasImage] ${formatJst(now)} ${EVENT_LABELS[record.event]} ${JSON.stringify(record)}`;
    if (record.event === "recovered" || record.event === "manual-retry") console.info(message);
    else console.warn(message);
  } catch {
    // 診断の失敗を利用者の作業へ伝播させず、例外本文もログへ流さない。
  }
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

const VERSION_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/;
const count = z.number().int().min(0).max(1_000_000);
const RecordSchema = z
  .object({
    appVersion: z.union([z.literal("unknown"), z.string().regex(VERSION_PATTERN)]),
    complete: z.boolean(),
    displayHeight: count,
    displayWidth: count,
    elapsedMs: z
      .number()
      .finite()
      .min(0)
      .max(7 * 24 * 60 * 60 * 1_000),
    event: z.enum(["error", "timeout", "recovered", "manual-retry", "pending-timeout"]),
    imageCount: count,
    naturalHeight: count,
    naturalWidth: count,
    objectCount: count,
    online: z.boolean(),
    retries: z.number().int().min(0).max(2),
    sourceKind: z.enum(["placeholder", "blob", "remote"]),
    visibility: z.enum(["hidden", "visible"]),
    zoom: z.number().finite().min(0).max(1_000),
  })
  .strict();
type Event = z.infer<typeof RecordSchema>["event"];
const EVENT_LABELS: Readonly<Record<Event, string>> = {
  error: "画像の読み込み失敗",
  timeout: "画像の読み込み待機超過",
  recovered: "画像の読み込み復旧",
  "manual-retry": "画像の手動再試行",
  "pending-timeout": "画像のアップロード確定待ち",
};
let rateWindowStartedAt = 0;
let recordsInWindow = 0;

function formatJst(timestamp: number): string {
  return `${new Date(timestamp + 9 * 60 * 60 * 1_000).toISOString().slice(0, 19).replace("T", " ")} JST`;
}
